import { useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { useNavActions } from '../../../hooks/useNavActions';
import '../../backlog/backlog.css';
import './training.css';
import {
  TRAINING_RECORDS, EMPLOYEES, CATEGORIES, DOC_TYPES, STATUSES, CURRENT_USER,
  EMPTY_FILTERS, REQUIRED_BY_CATEGORY, PAGE_SIZES,
  filterRecords, subjectOf, missingRequired, signedPayload, sha256, blankRecord, stamp,
  employeeLabel, qualificationOf, certSubject, certificateNoFor, list,
  type TrainingRecord, type TrainingFilters, type Category, type AuditRow,
} from './trainingData';
import { CheckedCombo } from '../../../components/CheckedCombo';
import {
  useTestNames, useMethodNames, optionsFor, FALLBACK_TESTS, FALLBACK_METHODS,
} from './trainingLookups';

// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Training Records
//
// Ported from the approved prototype (alpaca_training_demo_v26.html,
// 2026-08-14): employee panel, filter bar, quick search, records grid, and the
// record dialog with General Information, category-driven detail fields,
// evidence, QA review, electronic signature and audit trail — plus the report
// and certificate views.
//
// Rendered with THIS product's shared components (FormTitle / AppToolbar /
// AppDataGrid / useNotify / useConfirm / statusVariant) rather than the
// prototype's own CSS, so it sits alongside every other form (CLAUDE.md
// standing rules 7, 8 and 9). Everything the shared components DON'T own is in
// training.css — token-driven, never a hardcoded brand hex.
//
// Design review 2026-08-14 applied: the fuzzy search is its own row directly
// above the grid; the record dialog opens without a scrollbar (compact rows,
// the audit trail scrolls inside its own card); accent-coloured section titles,
// dark dialog title bars and a branded report + certificate replace the
// monotone first cut.
//
// ⚠️ NOT PERSISTED. Edits live in component state and are lost on reload; Save
// reports what WOULD be saved. See trainingData.ts for what changes when it is
// wired up — in particular the sequential record id (§14a Rule B).
// ─────────────────────────────────────────────────────────────────────────────

/** Every required control carries the tint, not only the empty ones — the
 *  approved design shows the mandatory set at a glance. `missing` still drives
 *  the red asterisk and the pre-signature check, so an empty one is not merely
 *  a shade difference. */
const reqClass = 'tr-in req';

function Row({ label, required, children }:
  { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="tr-row">
      {/* The asterisk lives in its own element so the label's own text stays
          exactly the field name — queries (and screen readers) read "Date",
          not "Date*". */}
      <label>{label}{required && <span className="tr-star">*</span>}</label>
      <div>{children}</div>
    </div>
  );
}

function Card({ title, note, className, children }:
  { title: string; note?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={`tr-card${className ? ` ${className}` : ''}`}>
      <div className="tr-card-t">
        {title}
        {note && <span className="tr-note">{note}</span>}
      </div>
      {children}
    </div>
  );
}

/** Em-dash for an empty cell — a blank cell is indistinguishable from a column
 *  that failed to load. */
const dash = (v: string | undefined | null) => (v && String(v).trim() ? String(v) : '—');

export default function TrainingRecordsPage() {
  const notify = useNotify();
  const confirm = useConfirm();
  // MAC: New Training Record → Create · Save Draft / Sign → Write (Create for a new
  // record). Hidden, never greyed; Verify / View Report / Certificate preview stay.
  const perm = useNavActions('training-records');

  const [records, setRecords] = useState<TrainingRecord[]>(TRAINING_RECORDS);
  const [filters, setFilters] = useState<TrainingFilters>(EMPTY_FILTERS);
  const [empSearch, setEmpSearch] = useState('');

  const [editing, setEditing] = useState<TrainingRecord | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [report, setReport] = useState<TrainingRecord | null>(null);
  const [cert, setCert] = useState<TrainingRecord | null>(null);

  const rows = useMemo(() => filterRecords(records, filters), [records, filters]);

  /** Row number shown in the "#" column. Built once per result set rather than
   *  an indexOf per cell, and keyed off the FILTERED list so the numbers always
   *  run 1..n for what is actually on screen. */
  const seq = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r, i) => m.set(r.id, i + 1));
    return m;
  }, [rows]);

  /** Counts for the summary chips — of the filtered set, so they describe what
   *  the grid is showing rather than the whole table. */
  const counts = useMemo(() => ({
    total:     rows.length,
    qualified: rows.filter(r => r.status === 'Qualified').length,
    pending:   rows.filter(r => r.status === 'Pending QA Review').length,
    failed:    rows.filter(r => r.status === 'Failed').length,
  }), [rows]);

  const patch = (changes: Partial<TrainingRecord>) =>
    setEditing(prev => (prev ? { ...prev, ...changes } : prev));

  const visibleEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    return q ? EMPLOYEES.filter(e => `${e.id} ${e.name}`.toLowerCase().includes(q)) : EMPLOYEES;
  }, [empSearch]);

  // ── actions ───────────────────────────────────────────────────────────────

  /** Open a record. A record that shipped already signed carries no stored hash,
   *  so stamp one on open — otherwise the block shows a green "Signed — Pass"
   *  seal above the words "not signed", and Verify has nothing to compare
   *  against. (The real system stores the hash written at signing time; this
   *  back-fill exists only because the sample data has none.) */
  const openRecord = async (r: TrainingRecord) => {
    setIsNew(false);
    if (r.qa.signed && !r.qa.hash) {
      const hash = await sha256(signedPayload(r));
      const stamped: TrainingRecord = { ...r, qa: { ...r.qa, hash } };
      setRecords(prev => prev.map(x => (x.id === stamped.id ? stamped : x)));
      setEditing(stamped);
      return;
    }
    setEditing({ ...r });
  };

  const newRecord = () => { setEditing(blankRecord(records)); setIsNew(true); };

  const saveDraft = async () => {
    if (!editing) return;
    const ok = await confirm.confirm({ title: 'Save this training record?', message: 'Do you want to Save?' });
    if (!ok) return;
    setRecords(prev => isNew ? [editing, ...prev] : prev.map(r => (r.id === editing.id ? editing : r)));
    setEditing(null);
    // Honest: nothing is stored, and claiming otherwise would have someone close
    // the browser believing their work was kept.
    notify.info(`${editing.id} is not persisted yet — this screen is a layout preview.`);
  };

  /** Sign the QA review. Refuses on incomplete data BEFORE producing a hash: a
   *  signature over a half-filled record is worse than no signature, because it
   *  looks like a completed review. */
  const sign = async () => {
    if (!editing) return;
    const missing = missingRequired(editing);
    if (missing.length) { notify.error(`Complete these first: ${missing.join(', ')}.`); return; }
    if (!editing.qa.status) { notify.error('Select Pass or Fail before signing.'); return; }
    if (editing.qa.status === 'Fail' && !editing.qa.comment.trim()) {
      // A failure with no reason is unauditable — the whole point of the record.
      notify.error('A comment is required when the review result is Fail.');
      return;
    }
    const ok = await confirm.confirm({
      title: 'Apply electronic signature?',
      message: `You are signing as ${CURRENT_USER.name} (${CURRENT_USER.role}). `
             + `This records a QA review of ${editing.id} with result "${editing.qa.status}" `
             + 'and stamps a SHA-256 over the record as it stands now.',
      confirmLabel: 'Sign',
    });
    if (!ok) return;

    const when = stamp();
    const status = editing.qa.status === 'Pass' ? 'Qualified' : 'Failed';
    const audit: AuditRow[] = [...editing.audit,
      [when, CURRENT_USER.name, CURRENT_USER.role, 'QA Review', editing.qa.status]];

    // Hash the record as it will BE once signed, not as it was a moment before.
    // `status` is part of the signed payload and signing is what changes it
    // (Pending QA Review → Qualified), so hashing the pre-sign state made every
    // freshly-signed record verify as INVALID — a signature that never holds is
    // worse than none, because the failure looks like tampering.
    const signedRecord: TrainingRecord = { ...editing, status };
    const hash = await sha256(signedPayload(signedRecord));

    patch({
      status,
      qa: { ...editing.qa, date: when, id: CURRENT_USER.name, signed: true, hash },
      audit,
    });
    notify.success(`Signed. ${editing.id} is now ${status}.`);
  };

  /** Open the signed report. Refuses on an unsigned record: the report is headed
   *  "Signed Training Record Report", so producing one for an unreviewed record
   *  would put that title over an unsigned record. */
  const openReport = () => {
    if (!editing) return;
    if (!editing.qa.signed) { notify.error('Sign first to generate the official report.'); return; }
    setReport(editing);
  };

  /** Issue the certificate.
   *
   *  This is the one-way step the preview exists to protect: it mints the
   *  certificate number, stamps who issued it and when, and writes an audit row.
   *  Re-issuing is refused rather than silently minting a second number for the
   *  same qualification — two certificates for one record cannot both be the
   *  record of truth. */
  const issueCertificate = async () => {
    if (!cert) return;
    const target = records.find(r => r.id === cert.id) ?? cert;
    if (target.certificateNo) {
      notify.error(`Certificate ${target.certificateNo} has already been issued for ${target.id}.`);
      setCert(target);
      return;
    }
    const no = certificateNoFor(target);
    const ok = await confirm.confirm({
      title: 'Issue this certificate?',
      message: `Certificate ${no} will be issued for ${target.id} (${target.employee}). `
             + 'An issued certificate is numbered, dated and cannot be issued again.',
      confirmLabel: 'Issue Certificate',
    });
    if (!ok) return;

    const when = stamp();
    const issued: TrainingRecord = {
      ...target,
      certificateNo: no,
      certificateIssuedOn: when,
      certificateIssuedBy: 'ALPACA LIMS',
      audit: [...target.audit,
        [when, CURRENT_USER.name, CURRENT_USER.role, 'Issue Certificate', `Certificate ${no} issued`]],
    };
    setRecords(prev => prev.map(r => (r.id === issued.id ? issued : r)));
    // Keep the open dialogs on the SAME record object, so the certificate
    // switches from watermarked preview to issued in place and the record
    // dialog's audit trail shows the new row without reopening.
    setCert(issued);
    setEditing(prev => (prev && prev.id === issued.id ? issued : prev));
    notify.success(`Certificate ${no} issued.`);
  };

  /** Recompute the hash and compare. This is the check that makes the signature
   *  mean something — without it a signed record could be edited freely. */
  const verify = async () => {
    if (!editing) return;
    if (!editing.qa.signed) { notify.error('This record has not been signed yet.'); return; }
    const now = await sha256(signedPayload(editing));
    if (now === editing.qa.hash) notify.success('Signature valid — the record matches what was signed.');
    else notify.error('Signature INVALID — the record has changed since it was signed.');
  };

  // ── grid ──────────────────────────────────────────────────────────────────

  const cols: AppColumn<TrainingRecord>[] = [
    { field: 'seq', header: '#', width: '46px', align: 'right',
      sortable: false, filterable: false, noExport: true,
      text: r => String(seq.get(r.id) ?? ''),
      value: r => seq.get(r.id) ?? 0 },
    { field: 'id', header: 'Record ID', width: '135px',
      render: r => (
        <button className="ca-btn-link" onClick={e => { e.stopPropagation(); openRecord(r); }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                   color: 'var(--ai-ink)', fontWeight: 600, textDecoration: 'underline' }}>
          {r.id}
        </button>
      ) },
    { field: 'employee', header: 'Employee', width: '185px',
      // Employee id + name, matching the design. Rendered as two nodes so the
      // name remains its own text run (the id is a prefix, not part of it).
      text: r => employeeLabel(r.employee),
      render: r => (
        <span><span className="tr-emp-id">{EMPLOYEES.find(e => e.name === r.employee)?.id ?? '—'}</span>
          {' - '}<span>{r.employee}</span></span>
      ) },
    { field: 'category', header: 'Category', width: '110px' },
    { field: 'type',     header: 'Type',     width: '90px'  },
    { field: 'subject',  header: 'Test / SOP / Description', width: '215px', text: r => subjectOf(r) },
    { field: 'job',      header: 'JobID',      width: '105px', text: r => dash(r.job) },
    { field: 'qb',       header: 'QCBatchID',  width: '150px', text: r => dash(r.qb) },
    { field: 'date',     header: 'Date',       width: '100px' },
    // type:'status' hands the pill to the SHARED statusVariant mapping, so
    // "Qualified" is the same green here as everywhere else (standing rule 8).
    { field: 'status',   header: 'Status',     width: '145px', type: 'status' },
    { field: 'expiration', header: 'Expiration', width: '100px', text: r => dash(r.expiration) },
    { field: 'files', header: 'Evidence', width: '90px',
      text: r => (r.files.length ? `${r.files.length} file(s)` : 'None'),
      render: r => r.files.length
        ? <span title={r.files.join(', ')}>📎 {r.files.length}</span>
        : <span style={{ color: 'var(--ai-muted)' }}>—</span> },
    { field: 'action', header: 'Action', width: '80px', sortable: false, filterable: false, noExport: true,
      text: () => '',
      render: r => (
        <button className="ca-btn" onClick={e => { e.stopPropagation(); openRecord(r); }}>View</button>
      ) },
  ];

  const setF = (k: keyof TrainingFilters, v: string) => setFilters(prev => ({ ...prev, [k]: v }));

  /** Re-applies the criteria already in the boxes. Filtering runs on every
   *  keystroke, so this is what the Search button and the magnifier actually
   *  do — they are the familiar affordance, not the only way in. */
  const rerunSearch = () => setFilters(prev => ({ ...prev }));

  // Keyboard shortcut (CLAUDE.md standing rule 14, §18.6, ADR-0013): New Training
  // Record — alt+n, mirroring the toolbar's form-specific New button.  The shared
  // <AppToolbar> already binds Refresh + Export; the record dialog's Save / Sign /
  // Verify / etc. live inside a modal and are intentionally not bound here.
  // Registered unconditionally, before the first return.
  useHotkeys([
    { combo: 'alt+n', label: 'New Training Record', group: 'Training Records', order: 1,
      disabled: !perm.create, run: () => newRecord() },
  ]);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>QA/QC</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Training Records</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="training-records" />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Employee-centred training and qualification records — layout preview, nothing is saved yet.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={perm.create ? (
              <button className="ca-btn ca-btn-solid" onClick={newRecord}
                title={`New Training Record (${formatCombo('alt+n')})`}>
                <i className="ti ti-plus" />New Training Record
              </button>
            ) : undefined}
            exportData={{
              filename: 'training-records',
              headers: cols.filter(c => !c.noExport).map(c => String(c.header)),
              rows: rows.map(r => [r.id, employeeLabel(r.employee), r.category, r.type, subjectOf(r),
                                   dash(r.job), dash(r.qb), r.date, r.status, dash(r.expiration),
                                   r.files.length ? `${r.files.length} file(s)` : 'None']),
            }}
            onRefresh={() => { setRecords(TRAINING_RECORDS); setFilters(EMPTY_FILTERS);
                               notify.success('Refreshed.'); }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', minHeight: 0, flex: 1 }}>

          {/* ── employees ─────────────────────────────────────────────────── */}
          <aside className="bl-card" style={{ width: 250, flexShrink: 0, display: 'flex',
                                              flexDirection: 'column', minHeight: 0 }}>
            <div className="tr-emp-hdr">Employees</div>
            <div className="tr-emp-find">
              <input className="tr-in" placeholder="Find employee…" value={empSearch}
                onChange={e => setEmpSearch(e.target.value)} />
            </div>
            <div className="tr-emp-list">
              <button className={`tr-emp-item${filters.employee === '' ? ' active' : ''}`}
                onClick={() => setF('employee', '')}>
                All Employees <span className="tr-emp-sub">({records.length})</span>
              </button>
              {visibleEmployees.map(e => (
                <button key={e.id} onClick={() => setF('employee', e.name)}
                  className={`tr-emp-item${filters.employee === e.name ? ' active' : ''}`}>
                  <span className="tr-emp-id">{e.id}</span>{' - '}<span>{e.name}</span>
                </button>
              ))}
              {visibleEmployees.length === 0 && (
                <div className="tr-emp-none">No employee matches “{empSearch}”.</div>
              )}
            </div>
          </aside>

          {/* ── filters + grid ────────────────────────────────────────────── */}
          <section className="bl-card bl-card-flex" style={{ flex: 1, minWidth: 0 }}>

            {/* Structured filters. */}
            <div className="tr-filters">
              <div>
                <div className="tr-f-lbl">Record ID</div>
                <input className="tr-in" style={{ width: 150 }} placeholder="Enter Record ID"
                  value={filters.recordId} onChange={e => setF('recordId', e.target.value)} />
              </div>
              <div>
                <div className="tr-f-lbl">Category</div>
                <select className="tr-in" style={{ width: 150 }} value={filters.category}
                  onChange={e => setF('category', e.target.value)}>
                  <option value="">All</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div className="tr-f-lbl">Status</div>
                <select className="tr-in" style={{ width: 165 }} value={filters.status}
                  onChange={e => setF('status', e.target.value)}>
                  <option value="">All</option>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div className="tr-f-lbl">Date From</div>
                <input type="date" className="tr-in" style={{ width: 150 }} value={filters.from}
                  onChange={e => setF('from', e.target.value)} />
              </div>
              <div>
                <div className="tr-f-lbl">To</div>
                <input type="date" className="tr-in" style={{ width: 150 }} value={filters.to}
                  onChange={e => setF('to', e.target.value)} />
              </div>
              {/* Filtering is live as you type (the prototype's inputs are wired
                  the same way), so Search re-runs the current criteria rather
                  than being the only way to apply them. */}
              <button className="ca-btn ca-btn-solid" onClick={rerunSearch}>
                <i className="ti ti-search" />Search
              </button>
              <button className="ca-btn" onClick={() => setFilters(EMPTY_FILTERS)}>
                <i className="ti ti-x" />Clear
              </button>
            </div>

            {/* Fuzzy search — its OWN row directly above the grid (design
                review 2026-08-14). Filtering is live, so the button is a
                visual affordance that simply re-focuses; there is nothing to
                submit. */}
            <div className="tr-quick">
              <div className="tr-quick-wrap">
                <input className="tr-in" placeholder="Quick search..."
                  aria-label="Quick search"
                  title="Fuzzy search across record id, employee, test, method, job and batch"
                  value={filters.quick} onChange={e => setF('quick', e.target.value)} />
                <button className="ca-btn ca-btn-solid tr-quick-btn" onClick={rerunSearch}
                  title="Fuzzy Search" aria-label="Fuzzy Search">
                  <i className="ti ti-search" />
                </button>
              </div>
              {filters.quick && (
                <button className="ca-btn tr-quick-clear" onClick={() => setF('quick', '')}>
                  <i className="ti ti-x" />Clear
                </button>
              )}
            </div>

            {/* Summary of what the grid is showing. */}
            <div className="tr-sum">
              <span className="tr-sum-title">
                {filters.employee
                  ? `Training Records — ${employeeLabel(filters.employee)}`
                  : 'Recent Training Records'}
              </span>
              <span className="tr-chip">Total<b>{counts.total}</b></span>
              <span className="tr-chip ok">Qualified<b>{counts.qualified}</b></span>
              <span className="tr-chip warn">Pending QA<b>{counts.pending}</b></span>
              <span className="tr-chip alert">Failed<b>{counts.failed}</b></span>
              <span className="tr-sum-count">{rows.length} of {records.length} records</span>
              <span className="tr-sum-hint">Click Record ID or View · Double-click a row</span>
            </div>

            <div style={{ flex: 1, minHeight: 0, padding: 12 }}>
              {/* AppDataGrid owns the ONE footer (page X of N + page size) —
                  no second count/pagination bar underneath it (§18.6). */}
              <AppDataGrid<TrainingRecord>
                rows={rows}
                columns={cols}
                getRowId={r => r.id}
                defaultPageSize={20}
                pageSizeOptions={PAGE_SIZES}
                onRowClick={openRecord}
                emptyText="No training records match the current filters."
              />
            </div>
          </section>
        </div>
      </div>

      {editing && (
        <RecordDialog
          record={editing} isNew={isNew}
          canEdit={isNew ? perm.create : perm.write}
          onChange={patch}
          onClose={() => setEditing(null)}
          onSave={() => void saveDraft()}
          onSign={() => void sign()}
          onVerify={() => void verify()}
          onReport={openReport}
          onCertificate={() => setCert(editing)}
        />
      )}

      {report && <ReportDialog record={report} onClose={() => setReport(null)} />}
      {cert && (
        <CertificateDialog record={cert} onClose={() => setCert(null)}
          onIssue={() => void issueCertificate()} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialogs
// ─────────────────────────────────────────────────────────────────────────────

function Overlay({ children, onClose, width = 1240 }:
  { children: React.ReactNode; onClose: () => void; width?: number }) {
  return (
    <div className="tr-ov" onClick={onClose}>
      <div className="tr-dlg" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true"
        style={{ width }}>
        {children}
      </div>
    </div>
  );
}

function DialogHeader({ title, children, onClose }:
  { title: string; children?: React.ReactNode; onClose: () => void }) {
  return (
    <div className="tr-dlg-hdr">
      <b>{title}</b>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {children}
        <button className="tr-dlg-x" onClick={onClose} aria-label="Close">✕</button>
      </div>
    </div>
  );
}

function RecordDialog({ record, isNew, canEdit, onChange, onClose, onSave, onSign, onVerify, onReport, onCertificate }: {
  record: TrainingRecord; isNew: boolean;
  /** MAC: a new record needs Create, an existing one Write. Without it the Save /
   *  Sign / Attach actions are hidden (never greyed). */
  canEdit: boolean;
  onChange: (c: Partial<TrainingRecord>) => void;
  onClose: () => void; onSave: () => void; onSign: () => void; onVerify: () => void;
  onReport: () => void; onCertificate: () => void;
}) {
  const r = record;
  const req = REQUIRED_BY_CATEGORY[r.category] ?? [];
  const missing = missingRequired(r);

  // Checked-combo sources: the lab's own Test Code library and Method form,
  // NOT a list kept here (design review 2026-08-14). Cached per session; the
  // built-in sample list stands in until they load so a required combo is never
  // empty and un-passable.
  const testNames = useTestNames();
  const methodNames = useMethodNames();
  const lookupFor = (key: keyof TrainingRecord, current: string[]): string[] =>
    key === 'tests'
      ? optionsFor(testNames.data, FALLBACK_TESTS, current)
      : optionsFor(methodNames.data, FALLBACK_METHODS, current);

  // A record that already exists is HISTORY: what was trained, when, and on
  // what. It is reviewed and signed — never retro-edited. Only a brand-new
  // record is authored here, so everything below is read-only otherwise.
  const locked = !isNew;
  // Once signed, even the QA review closes: the SHA-256 covers the result and
  // the comment, so editing either after signing would leave a record whose own
  // signature reports it as tampered with.
  const qaLocked = locked && r.qa.signed;

  /** Read-only fields render as a plain disabled box — a combo the user cannot
   *  use still LOOKS like an invitation to change the value. */
  const ro = (value: string) => <input className="tr-in" value={value} readOnly />;

  return (
    <Overlay onClose={onClose}>
      <DialogHeader title={`Training Record ${r.id}${isNew ? ' (new)' : ''}`} onClose={onClose}>
        {locked && (
          <span className="tr-ro-badge">
            <i className="ti ti-lock" /> {r.qa.signed ? 'Signed — read only' : 'History — read only'}
          </span>
        )}
      </DialogHeader>

      <div className="tr-dlg-body">
        <div className="tr-2col">
          <Card title="General Information">
            <Row label="Record ID">{ro(r.id)}</Row>
            {/* The record belongs to ONE employee. Offering all 24 in a combo on
                an existing record invites reassigning someone else's training
                history to a different analyst. */}
            <Row label="Employee" required={isNew}>
              {isNew ? (
                <select className={reqClass} value={r.employee}
                  onChange={e => onChange({ employee: e.target.value })}>
                  <option value="">Select…</option>
                  {EMPLOYEES.map(e => <option key={e.id} value={e.name}>{e.id} - {e.name}</option>)}
                </select>
              ) : ro(employeeLabel(r.employee))}
            </Row>
            <Row label="Category" required={isNew}>
              {isNew ? (
                <select className={reqClass} value={r.category}
                  onChange={e => {
                    const category = e.target.value as Category;
                    // Reset the type to something valid for the new category, or the
                    // record keeps a DOC type while claiming to be an SOP.
                    onChange({ category, type: category === 'DOC' ? 'IDOC' : category === 'SOP' ? 'SOP' : 'Safety' });
                  }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : ro(r.category)}
            </Row>
            <Row label="Date" required={isNew}>
              {isNew
                ? <input type="date" className={reqClass} value={r.date}
                    onChange={e => onChange({ date: e.target.value })} />
                : ro(r.date)}
            </Row>
            <Row label="Status">{ro(r.status)}</Row>
            <Row label="Expiration">
              {isNew
                ? <input type="date" className="tr-in" value={r.expiration}
                    onChange={e => onChange({ expiration: e.target.value })} />
                : ro(r.expiration || '—')}
            </Row>
          </Card>

          <Card title={`${r.category} Details`}>
            {r.category === 'DOC' && (
              <Row label="DOC Type" required={isNew}>
                {isNew ? (
                  <select className={reqClass} value={r.type} onChange={e => onChange({ type: e.target.value })}>
                    {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : ro(r.type)}
              </Row>
            )}
            {req.filter(f => f.key !== 'type').map(f => {
              const raw = r[f.key];
              // Lookup-backed fields are MULTI-select: one DOC covers several
              // test codes / methods, so the value is a list.
              if (f.lookup) {
                const picked = Array.isArray(raw) ? raw as string[] : [];
                const loading = f.key === 'tests' ? testNames.isLoading : methodNames.isLoading;
                return (
                  <Row key={String(f.key)} label={f.label} required={isNew}>
                    {!isNew ? ro(list(picked) || '—') : (
                      <CheckedCombo
                        options={lookupFor(f.key, picked)}
                        values={picked}
                        loading={loading}
                        required
                        ariaLabel={f.label}
                        unit={f.key === 'tests' ? 'tests' : 'methods'}
                        onChange={v => onChange({ [f.key]: v } as Partial<TrainingRecord>)}
                      />
                    )}
                  </Row>
                );
              }
              const value = String(raw ?? '');
              return (
                <Row key={String(f.key)} label={f.label} required={isNew}>
                  {!isNew ? ro(value || '—') : (
                    <input className={reqClass} value={value}
                      onChange={e => onChange({ [f.key]: e.target.value } as Partial<TrainingRecord>)} />
                  )}
                </Row>
              );
            })}
            {r.category === 'SOP' && (
              <Row label="Trainer">
                {isNew
                  ? <input className="tr-in" value={r.trainer ?? ''}
                      onChange={e => onChange({ trainer: e.target.value })} />
                  : ro(r.trainer || '—')}
              </Row>
            )}
            <Row label="Evidence">
              <div>
                {isNew && canEdit && (
                  <button className="ca-btn ca-btn-solid"
                    onClick={() => onChange({ files: [...r.files, `Evidence_${r.files.length + 1}.pdf`] })}>
                    <i className="ti ti-paperclip" />Attach File(s)
                  </button>
                )}
                <div className="tr-files">
                  {r.files.length === 0 ? 'No evidence attached' : r.files.map((f, i) => (
                    <div key={f + i}>
                      📎 {f}{' '}
                      {isNew && canEdit && (
                        <button className="tr-file-x"
                          onClick={() => onChange({ files: r.files.filter((_, j) => j !== i) })}>
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Row>
          </Card>
        </div>

        <Card title="QA Review">
          <table className="bl-tbl" style={{ width: '100%' }}>
            <thead><tr><th>Date</th><th>ID</th><th>Role</th><th>Action</th><th>Status</th><th>Comment</th></tr></thead>
            <tbody>
              <tr>
                <td style={{ minWidth: 120 }}>{r.qa.date || '—'}</td>
                <td>{r.qa.id || '—'}</td>
                <td>{r.qa.role}</td>
                <td>Review</td>
                {/* The review itself stays open on an unsigned record — that is
                    the one thing a reviewer is here to do — and closes the moment
                    it is signed. */}
                <td style={{ minWidth: 96 }}>
                  {qaLocked ? ro(r.qa.status || '—') : (
                    <select className={reqClass} value={r.qa.status}
                      onChange={e => onChange({ qa: { ...r.qa, status: e.target.value as 'Pass' | 'Fail' | '' } })}>
                      <option value="">Select…</option>
                      <option>Pass</option>
                      <option>Fail</option>
                    </select>
                  )}
                </td>
                <td>
                  {qaLocked ? ro(r.qa.comment || '—') : (
                    <input className="tr-in" placeholder="Comment" value={r.qa.comment}
                      onChange={e => onChange({ qa: { ...r.qa, comment: e.target.value } })} />
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className={`tr-sig ${r.qa.signed ? 'signed' : 'pending'}`}>
            <div className="tr-sig-hdr">
              <b><i className="ti ti-writing-sign" />Electronic Signature</b>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="tr-sig-seal">
                  <i className={`ti ${r.qa.signed ? 'ti-shield-check' : 'ti-clock'}`} />
                  {r.qa.signed ? `Signed — ${r.qa.status}` : 'Awaiting signature'}
                </span>
                {canEdit && (
                  <button className="ca-btn ca-btn-solid" onClick={onSign} disabled={r.qa.signed}>
                    <i className="ti ti-signature" />
                    {r.qa.signed ? 'Signed' : 'Sign'}
                  </button>
                )}
              </span>
            </div>
            {missing.length > 0 && !r.qa.signed && (
              <div className="bl-err" style={{ margin: '8px 10px 0' }}>
                <i className="ti ti-alert-triangle" style={{ fontSize: 13, marginRight: 5, verticalAlign: 'middle' }} />
                Required before signing: {missing.join(', ')}.
              </div>
            )}
            <div className="tr-sig-grid">
              {([
                ['User Identity', r.qa.id || '—'],
                ['Role', r.qa.role],
                ['Action', 'Review'],
                ['Status', r.qa.signed ? `Signed — ${r.qa.status}` : 'Not Signed'],
                ['Date / Time', r.qa.date || '—'],
              ] as [string, string][]).map(([k, v]) => (
                <div className="tr-sig-cell" key={k}>
                  <div className="tr-sig-k">{k}</div>
                  <div className="tr-sig-v">{v}</div>
                </div>
              ))}
              <div className="tr-sig-cell tr-sig-wide">
                <div className="tr-sig-k">Meaning of Action</div>
                <div className="tr-sig-v">QA review of this training record.</div>
              </div>
              <div className="tr-sig-cell tr-sig-wide tr-sig-hashwrap">
                <div className="tr-sig-k">SHA-256 · record fingerprint</div>
                <code data-testid="sig-hash" className="tr-sig-hash">
                  {r.qa.hash || 'Not signed — no fingerprint has been stamped for this record.'}
                </code>
              </div>
            </div>
            <div className="tr-sig-actions">
              <button className="ca-btn" onClick={onVerify}>Verify</button>
              <button className="ca-btn" onClick={onReport}>View Report</button>
              <button className="ca-btn" onClick={onCertificate} disabled={!r.qa.signed || r.qa.status !== 'Pass'}
                title={r.qa.signed && r.qa.status === 'Pass'
                  ? 'Preview the qualification certificate'
                  : 'A certificate can only be issued for a signed, passing review'}>
                Preview Certificate
              </button>
            </div>
          </div>
        </Card>

        <Card title="Audit Trail" note="(system generated)" className="tr-card-grow">
          <div className="tr-audit-scroll">
            <table className="bl-tbl" style={{ width: '100%' }}>
              <thead><tr><th>Date / Time</th><th>User</th><th>Role</th><th>Action</th><th>Meaning / Reason</th></tr></thead>
              <tbody>
                {r.audit.length === 0
                  ? <tr><td colSpan={5} style={{ color: 'var(--ai-muted)' }}>No audit entries yet.</td></tr>
                  : r.audit.map((a, i) => (
                      <tr key={i}>{a.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                    ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="tr-dlg-foot">
        <button className="ca-btn" onClick={onClose}>{qaLocked ? 'Close' : 'Cancel'}</button>
        {/* Nothing on a signed record can change, so offering Save would be a
            button that cannot do anything. */}
        {!qaLocked && canEdit && (
          <button className="ca-btn ca-btn-solid" onClick={onSave}>
            <i className="ti ti-device-floppy" />Save Draft
          </button>
        )}
      </div>
    </Overlay>
  );
}

function ReportDialog({ record: r, onClose }: { record: TrainingRecord; onClose: () => void }) {
  const line = (k: string, v: string) => (
    <div className="tr-rep-line"><b>{k}:</b> <span>{v || '—'}</span></div>
  );
  return (
    <Overlay onClose={onClose} width={860}>
      <DialogHeader title="Training Record Report" onClose={onClose}>
        <button className="ca-btn" onClick={onClose}>Close</button>
      </DialogHeader>
      <div className="tr-rep">
        <div className="tr-rep-brand">
          <img className="tr-rep-logo" src="/alpaca-logo.jpg" alt="" />
          <div>
            <div className="tr-rep-name">ALPACA LIMS</div>
            <div className="tr-rep-sub">Signed Training Record Report</div>
          </div>
        </div>
        <hr className="tr-rep-rule" />
        {line('Record', r.id)}
        {line('Employee', employeeLabel(r.employee))}
        {line('Training / Qualification', qualificationOf(r))}
        {line('Status', r.status)}
        {line('QA Officer', r.qa.id
          ? `${r.qa.id} · ${r.qa.status || '—'} · ${r.qa.date || '—'}`
          : 'Not reviewed')}
        {/* Reflects the real state — a report that always said "Not issued"
            would contradict the certificate it was printed alongside. */}
        {line('Certificate ID', r.certificateNo
          ? `${r.certificateNo} · issued ${r.certificateIssuedOn}`
          : 'Not issued')}
        <div className="tr-rep-line"><b>SHA-256:</b></div>
        <div className="tr-rep-hash">{r.qa.hash || '—'}</div>
      </div>
    </Overlay>
  );
}

function CertificateDialog({ record: r, onClose, onIssue }:
  { record: TrainingRecord; onClose: () => void; onIssue: () => void }) {
  const empId = EMPLOYEES.find(e => e.name === r.employee)?.id ?? '—';
  // The ONE thing that separates a preview from a certificate: whether a number
  // has been minted. Everything conditional below keys off this, so a preview
  // can never present itself with the trappings of an issued document.
  const issued = Boolean(r.certificateNo);
  const { headline, detail } = certSubject(r);

  const kv = (lines: [string, string][]) => (
    <div>
      {lines.map(([k, v]) => (
        <div className="tr-cert-line" key={k}><b>{k}</b><span>{v || '—'}</span></div>
      ))}
    </div>
  );

  return (
    <Overlay onClose={onClose} width={1080}>
      <DialogHeader title={issued ? 'Certificate' : 'Certificate Preview'} onClose={onClose}>
        <button className="ca-btn ca-btn-solid" onClick={onIssue} disabled={issued}
          title={issued ? `Already issued as ${r.certificateNo}` : 'Mint the certificate number'}>
          {issued ? 'Issued' : 'Issue Certificate'}
        </button>
        <button className="ca-btn" onClick={onClose}>Close</button>
      </DialogHeader>

      <div className="tr-cert-wrap">
        <div className="tr-cert">
          {/* Watermark ONLY while unissued — a screenshot of a preview must never
              pass for the real thing, and an issued certificate must not carry a
              disclaimer that says it isn't one. */}
          {!issued && (
            <div className="tr-cert-wm" aria-hidden="true"><span>PREVIEW · NOT ISSUED</span></div>
          )}

          <div className="tr-cert-inner">
            <div className="tr-cert-top">
              <div className="tr-cert-logo">
                <img src="/alpaca-logo.jpg" alt="" />
                <div className="tr-cert-brand">ALPACA LIMS</div>
              </div>
              <div className="tr-cert-h">
                <div className="tr-cert-h1">CERTIFICATE</div>
                <div className="tr-cert-h2">OF TRAINING &amp; QUALIFICATION</div>
              </div>
              <div className="tr-cert-seal-cell">
                <div className="tr-cert-seal">
                  <strong>✓</strong>
                  <span>{r.status === 'Qualified' ? 'QUALIFIED' : r.status.toUpperCase()}</span>
                </div>
              </div>
            </div>

            <hr className="tr-cert-rule" />
            <div className="tr-cert-idrow">
              <span className="tr-cert-idbox">
                Certificate ID: {r.certificateNo ?? 'Not issued'}
              </span>
            </div>

            <div className="tr-cert-body">
              {kv([
                ['Record ID', r.id],
                ['Employee ID', empId],
                ['Category', r.category],
                ['Type', r.category === 'DOC' ? r.type : r.category],
                ['Job ID', r.job ?? '—'],
                ['QC Batch ID', r.qb ?? '—'],
              ])}

              <div className="tr-cert-mid">
                <div className="tr-cert-lead">This is to certify that</div>
                <div className="tr-cert-name">{r.employee}</div>
                <div className="tr-cert-orule" />
                <div className="tr-cert-lead">
                  has successfully completed the training and is qualified in
                </div>
                {/* Headline is the METHOD / SOP number — "qualified in DOC" would
                    name the test type, not the thing they can now do. */}
                <div className="tr-cert-subject">{headline}</div>
                <div className="tr-cert-cat">{detail}</div>
              </div>

              {kv([
                ['Training Date', r.date],
                ['Expiration', r.expiration || '—'],
                ['Status', r.status.toUpperCase()],
                ['Review Result', (r.qa.status || '—').toUpperCase()],
              ])}
            </div>

            <div className="tr-cert-cols">
              <div>
                <div className="tr-cert-ct">QA REVIEW</div>
                <div className="tr-cert-cl">
                  Reviewed by: <b>{r.qa.id || '—'}</b><br />
                  Role: <b>{r.qa.role}</b><br />
                  Action: <b>Review</b><br />
                  Comment: <b>{r.qa.comment || '—'}</b>
                </div>
              </div>
              <div>
                <div className="tr-cert-ct">ELECTRONIC SIGNATURE</div>
                <div className="tr-cert-cl">
                  User Identity: <b>{r.qa.id || '—'}</b><br />
                  Role: <b>{r.qa.role}</b><br />
                  Status: <b>{r.qa.signed ? `Signed — ${r.qa.status}` : 'Not signed'}</b><br />
                  Date / Time: <b>{r.qa.date || '—'}</b><br />
                  SHA-256: <span className="tr-sig-hash">{r.qa.hash || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="tr-cert-foot">
            <span>System generated training certificate · ALPACA LIMS</span>
            <span>
              {issued
                ? `Certificate ${r.certificateNo} · Issued ${r.certificateIssuedOn}`
                : 'Preview — not saved / not issued'}
            </span>
          </div>
        </div>
      </div>
    </Overlay>
  );
}
